import type { DeliveryHistoryEntry, GiantId, PlayerEconomyState } from '../../domain';
import {
  createInitialPlayerEconomy,
  migrateDeliveryHistory,
  migratePoolEntry,
  migrateRatingFields,
  seedOpenedMapGiantIds,
  syncDeliveryPool,
  isGiantId,
} from '../../domain';
import type { StorageAdapter } from '../storage/StorageAdapter';
import {
  PLAYER_ECONOMY_HISTORY_CHUNK_KEYS,
  type StorageRecordKey,
} from '../storage/storageKeys';

const PLAYER_ECONOMY_KEY: StorageRecordKey = 'player_economy';

/** VK Storage max value length. */
export const VK_STORAGE_VALUE_MAX_BYTES = 4096;
export const PLAYER_ECONOMY_HISTORY_CAP = 40;
export const PLAYER_ECONOMY_HISTORY_CHUNK_SIZE = 8;

function parseDeliveryHistoryEntries(raw: unknown, now: number): DeliveryHistoryEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((entry) => ({
    deliveryId: String(entry?.deliveryId ?? ''),
    giantId: entry?.giantId as GiantId,
    orderIndex: Number(entry?.orderIndex),
    outcome: entry?.outcome === 'completed' ? 'completed' : 'failed',
    bestRating: Number(entry?.bestRating) || 0,
    lastRating:
      entry?.lastRating != null && typeof entry.lastRating === 'object'
        ? {
            timeScore: Number((entry.lastRating as { timeScore?: number }).timeScore) || 0,
            sparkScore: Number((entry.lastRating as { sparkScore?: number }).sparkScore) || 0,
            skillScore: Number((entry.lastRating as { skillScore?: number }).skillScore) || 0,
            totalScore: Number((entry.lastRating as { totalScore?: number }).totalScore) || 0,
            isPerfect: (entry.lastRating as { isPerfect?: boolean }).isPerfect === true,
          }
        : null,
    lastSparksCollected: Number(entry?.lastSparksCollected) || 0,
    lastSparksTotal: Number(entry?.lastSparksTotal) || 0,
    updatedAt: Number(entry?.updatedAt) || now,
  }));
}

export function capDeliveryHistory(history: DeliveryHistoryEntry[]): DeliveryHistoryEntry[] {
  if (history.length <= PLAYER_ECONOMY_HISTORY_CAP) {
    return history;
  }
  return [...history]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, PLAYER_ECONOMY_HISTORY_CAP);
}

function chunkDeliveryHistory(
  history: DeliveryHistoryEntry[],
): DeliveryHistoryEntry[][] {
  const chunks: DeliveryHistoryEntry[][] = [];
  for (let i = 0; i < history.length; i += PLAYER_ECONOMY_HISTORY_CHUNK_SIZE) {
    chunks.push(history.slice(i, i + PLAYER_ECONOMY_HISTORY_CHUNK_SIZE));
  }
  return chunks;
}

function parseOpenedMapGiantIds(data: Partial<PlayerEconomyState>): GiantId[] | undefined {
  if (!Array.isArray(data.openedMapGiantIds)) {
    return undefined;
  }
  return data.openedMapGiantIds.filter((id): id is GiantId => typeof id === 'string' && isGiantId(id));
}

function parsePlayerEconomy(raw: string | null, now: number): PlayerEconomyState {
  if (raw == null || raw === '') {
    return createInitialPlayerEconomy(now);
  }
  try {
    const data = JSON.parse(raw) as Partial<PlayerEconomyState>;
    const ratingFields = migrateRatingFields(data);
    const base: PlayerEconomyState = {
      totalRating: ratingFields.totalRating,
      ratingSum: ratingFields.ratingSum,
      ratedDeliveriesCount: ratingFields.ratedDeliveriesCount,
      perfectDeliveriesCount: Number(data.perfectDeliveriesCount) || 0,
      passesBalance: Math.max(0, Math.min(1000, Math.floor(Number(data.passesBalance) || 0))),
      streakDays: Number(data.streakDays) || 0,
      lastLoginDate: typeof data.lastLoginDate === 'string' ? data.lastLoginDate : null,
      lastDailyRewardClaimedDate:
        typeof data.lastDailyRewardClaimedDate === 'string' ? data.lastDailyRewardClaimedDate : null,
      dailyLoginStreakCompleted: data.dailyLoginStreakCompleted === true,
      urgentCompletedCount: Number(data.urgentCompletedCount) || 0,
      stickers: Array.isArray(data.stickers)
        ? data.stickers
            .map((item) => {
              if (item == null || typeof item !== 'object') {
                return null;
              }
              const id = (item as { id?: unknown }).id;
              const unlockedAt = (item as { unlockedAt?: unknown }).unlockedAt;
              if (typeof id !== 'string' || typeof unlockedAt !== 'number') {
                return null;
              }
              return { id, unlockedAt };
            })
            .filter((item): item is { id: string; unlockedAt: number } => item != null)
        : [],
      answeredQuizQuestionIds: Array.isArray(data.answeredQuizQuestionIds)
        ? data.answeredQuizQuestionIds.filter((id): id is string => typeof id === 'string')
        : [],
      quizDailyDate: typeof data.quizDailyDate === 'string' ? data.quizDailyDate : null,
      quizDailyAnsweredCount: Math.max(0, Math.floor(Number(data.quizDailyAnsweredCount) || 0)),
      notificationsRewardClaimed: data.notificationsRewardClaimed === true,
      vkSubscribeRewardClaimed: data.vkSubscribeRewardClaimed === true,
      deliveryPool: Array.isArray(data.deliveryPool)
        ? data.deliveryPool.map((entry) =>
            migratePoolEntry({
              ...entry,
              urgency:
                entry?.urgency === 'urgent' ||
                entry?.urgency === 'important' ||
                entry?.urgency === 'regular'
                  ? entry.urgency
                  : entry?.urgent === true
                    ? 'urgent'
                    : 'regular',
              waitingDay: Number(entry?.waitingDay) || 1,
              takenOnWaitingDay:
                typeof entry?.takenOnWaitingDay === 'number' ? entry.takenOnWaitingDay : null,
              passRewardClaimed:
                entry?.passRewardClaimed === true || entry?.urgentRewardClaimed === true,
              urgentExpiresAt:
                typeof entry?.urgentExpiresAt === 'number' ? entry.urgentExpiresAt : null,
              urgentRewardClaimed: entry?.urgentRewardClaimed === true,
            } as import('../../domain').DeliveryPoolEntry, now),
          )
        : [],
      deliveryPoolDate: typeof data.deliveryPoolDate === 'string' ? data.deliveryPoolDate : null,
      deliveryPoolExhausted: data.deliveryPoolExhausted === true,
      deliveryPackCompleteAckDate:
        typeof data.deliveryPackCompleteAckDate === 'string' ? data.deliveryPackCompleteAckDate : null,
      deliveryHistory: parseDeliveryHistoryEntries(data.deliveryHistory, now),
      openedMapGiantIds: parseOpenedMapGiantIds(data),
      lastPoolRotationAt:
        typeof data.lastPoolRotationAt === 'number' ? data.lastPoolRotationAt : now,
    };
    return seedOpenedMapGiantIds(syncDeliveryPool(base, now));
  } catch {
    return createInitialPlayerEconomy(now);
  }
}

async function loadHistoryChunks(
  storage: StorageAdapter,
  now: number,
): Promise<DeliveryHistoryEntry[]> {
  const parts = await Promise.all(
    PLAYER_ECONOMY_HISTORY_CHUNK_KEYS.map((key) => storage.get(key)),
  );
  const history: DeliveryHistoryEntry[] = [];
  for (const part of parts) {
    if (part == null || part === '') {
      continue;
    }
    try {
      history.push(...parseDeliveryHistoryEntries(JSON.parse(part), now));
    } catch {
      // skip a corrupt chunk
    }
  }
  return history;
}

export async function loadPlayerEconomy(
  storage: StorageAdapter,
  legacyPassesRaw: string | null,
  now = Date.now(),
): Promise<PlayerEconomyState> {
  const raw = await storage.get(PLAYER_ECONOMY_KEY);
  let state = parsePlayerEconomy(raw, now);

  if (raw == null && legacyPassesRaw != null && legacyPassesRaw !== '') {
    const legacy = Number(legacyPassesRaw);
    if (Number.isFinite(legacy)) {
      state = {
        ...state,
        passesBalance: Math.max(0, Math.min(1000, Math.floor(legacy))),
      };
    }
  }

  const inlineHistory = state.deliveryHistory ?? [];
  if (inlineHistory.length === 0) {
    const fromChunks = await loadHistoryChunks(storage, now);
    if (fromChunks.length > 0) {
      state = {
        ...state,
        deliveryHistory: fromChunks,
      };
    }
  }
  state = migrateDeliveryHistory(state);
  return {
    ...state,
    deliveryHistory: capDeliveryHistory(state.deliveryHistory ?? []),
  };
}

export async function savePlayerEconomy(
  storage: StorageAdapter,
  state: PlayerEconomyState,
): Promise<void> {
  const history = capDeliveryHistory(state.deliveryHistory ?? []);
  const main: PlayerEconomyState = {
    ...state,
    deliveryHistory: [],
  };
  await storage.set(PLAYER_ECONOMY_KEY, JSON.stringify(main));

  const chunks = chunkDeliveryHistory(history);
  await Promise.all(
    PLAYER_ECONOMY_HISTORY_CHUNK_KEYS.map((key, index) => {
      const chunk = chunks[index];
      if (chunk == null || chunk.length === 0) {
        return storage.remove(key);
      }
      return storage.set(key, JSON.stringify(chunk));
    }),
  );
}

export { PLAYER_ECONOMY_KEY };
